import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, AlertTriangle, Camera } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InspectionForm = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [photos, setPhotos] = useState([]);
  
  const [vehicleInfo, setVehicleInfo] = useState({
    mileage: '',
    transmission: '',
    interiorColor: '',
    bodyColor: '',
    engine: '',
    driveType: '',
    fuelType: ''
  });

  const [exteriorChecks, setExteriorChecks] = useState({
    backupCamera: { status: 'good', notes: '' },
    wiperBlades: { status: 'good', notes: '' },
    windshield: { status: 'good', notes: '' },
    bodyAlignment: { status: 'good', notes: '' },
    scratches: { status: 'good', notes: '' },
    dents: { status: 'good', notes: '' },
    paintCondition: { status: 'good', notes: '' },
    rust: { status: 'good', notes: '' },
    exteriorLights: { status: 'good', notes: '' },
    sunroof: { status: 'good', notes: '' },
    doorMirrors: { status: 'good', notes: '' },
    bumpers: { status: 'good', notes: '' },
    sideMoldings: { status: 'good', notes: '' },
    frameDamage: { status: 'good', notes: '' },
    glass: { status: 'good', notes: '' },
    bodySeals: { status: 'good', notes: '' }
  });

  const [interiorChecks, setInteriorChecks] = useState({
    steeringWheel: { status: 'good', notes: '' },
    horn: { status: 'good', notes: '' },
    cruiseControl: { status: 'good', notes: '' },
    seatCondition: { status: 'good', notes: '' },
    heatedSeats: { status: 'good', notes: '' },
    seatBelts: { status: 'good', notes: '' },
    clock: { status: 'good', notes: '' },
    radioNavigation: { status: 'good', notes: '' },
    innerDoorPanels: { status: 'good', notes: '' },
    powerLocks: { status: 'good', notes: '' },
    windowSwitches: { status: 'good', notes: '' },
    windowFunction: { status: 'good', notes: '' },
    centerConsole: { status: 'good', notes: '' },
    dash: { status: 'good', notes: '' },
    sunVisors: { status: 'good', notes: '' },
    dashLights: { status: 'good', notes: '' },
    dashGauges: { status: 'good', notes: '' },
    airConditioning: { status: 'good', notes: '' },
    heater: { status: 'good', notes: '' },
    rearDefroster: { status: 'good', notes: '' },
    carpet: { status: 'good', notes: '' },
    floorMats: { status: 'good', notes: '' },
    cigaretteUse: { status: 'good', notes: '' },
    unusualOdors: { status: 'good', notes: '' },
    cupHolders: { status: 'good', notes: '' },
    headliner: { status: 'good', notes: '' },
    blowerMotor: { status: 'good', notes: '' },
    interiorTrim: { status: 'good', notes: '' }
  });

  const [mechanicalChecks, setMechanicalChecks] = useState({
    airFilter: { status: 'good', notes: '' },
    batteryCondition: { status: 'good', notes: '' },
    chargingSystem: { status: 'good', notes: '' },
    coolingFanCondition: { status: 'good', notes: '' },
    radiatorCapSealing: { status: 'good', notes: '' },
    hoses: { status: 'good', notes: '' },
    radiator: { status: 'good', notes: '' },
    engineCoolant: { status: 'good', notes: '' },
    coolantLeaks: { status: 'good', notes: '' },
    belts: { status: 'good', notes: '' },
    powerSteering: { status: 'good', notes: '' },
    engineOil: { status: 'good', notes: '' },
    engineFluidLeaks: { status: 'good', notes: '' },
    transmission: { status: 'good', notes: '' },
    brakeSystem: { status: 'good', notes: '' },
    brakeFluidLeaks: { status: 'good', notes: '' },
    transferCaseLeaks: { status: 'good', notes: '' },
    differentialLeaks: { status: 'good', notes: '' },
    mufflerSystem: { status: 'good', notes: '' },
    cvJointsAxles: { status: 'good', notes: '' },
    shocksBushings: { status: 'good', notes: '' },
    ballJointsTieRods: { status: 'good', notes: '' },
    keyFob: { status: 'good', notes: '' }
  });

  const [roadTest, setRoadTest] = useState({
    roadTestPerformed: { status: 'good', notes: '' },
    enginePerformance: { status: 'good', notes: '' },
    transmissionPerformance: { status: 'good', notes: '' },
    differentialPerformance: { status: 'good', notes: '' },
    transferCasePerformance: { status: 'good', notes: '' },
    wheelBearingPerformance: { status: 'good', notes: '' },
    brakingPerformance: { status: 'good', notes: '' }
  });

  const [summary, setSummary] = useState('');
  const [inspectorNotes, setInspectorNotes] = useState('');

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Failed to load booking details');
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(`${API}/upload/photo`, formData);
        setPhotos(prev => [...prev, response.data.url]);
        toast.success('Photo uploaded');
      } catch (error) {
        console.error('Error uploading photo:', error);
        toast.error('Failed to upload photo');
      }
    }
  };

  const updateCheckStatus = (category, key, status) => {
    const setters = {
      exterior: setExteriorChecks,
      interior: setInteriorChecks,
      mechanical: setMechanicalChecks,
      roadTest: setRoadTest
    };
    
    setters[category](prev => ({
      ...prev,
      [key]: { ...prev[key], status }
    }));
  };

  const updateCheckNotes = (category, key, notes) => {
    const setters = {
      exterior: setExteriorChecks,
      interior: setInteriorChecks,
      mechanical: setMechanicalChecks,
      roadTest: setRoadTest
    };
    
    setters[category](prev => ({
      ...prev,
      [key]: { ...prev[key], notes }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/inspections`, {
        bookingId,
        vehicleInfo,
        exteriorChecks,
        interiorChecks,
        mechanicalChecks,
        roadTest,
        summary,
        inspectorNotes,
        photos
      });

      toast.success('Inspection report submitted successfully');
      setTimeout(() => {
        navigate(`/report/${bookingId}`);
      }, 1500);
    } catch (error) {
      console.error('Error submitting inspection:', error);
      toast.error('Failed to submit inspection report');
    } finally {
      setLoading(false);
    }
  };

  const renderCheckSection = (title, checks, category) => {
    const checkEntries = Object.entries(checks);
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-zinc-800">{title}</h3>
        <div className="space-y-4">
          {checkEntries.map(([key, check]) => (
            <div key={key} className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
              <div className="flex items-start justify-between mb-2">
                <Label className="text-zinc-300 font-medium capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => updateCheckStatus(category, key, 'good')}
                    className={`p-2 rounded-lg border transition-colors ${
                      check.status === 'good'
                        ? 'bg-green-500/20 border-green-500 text-green-500'
                        : 'border-zinc-700 text-zinc-500 hover:bg-white/5'
                    }`}
                    data-testid={`${category}-${key}-good`}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => updateCheckStatus(category, key, 'issue')}
                    className={`p-2 rounded-lg border transition-colors ${
                      check.status === 'issue'
                        ? 'bg-red-500/20 border-red-500 text-red-500'
                        : 'border-zinc-700 text-zinc-500 hover:bg-white/5'
                    }`}
                    data-testid={`${category}-${key}-issue`}
                  >
                    <AlertTriangle className="w-5 h-5" />
                  </button>
                </div>
              </div>
              {check.status === 'issue' && (
                <Textarea
                  placeholder="Describe the issue..."
                  value={check.notes}
                  onChange={(e) => updateCheckNotes(category, key, e.target.value)}
                  className="mt-2 bg-zinc-900 border-zinc-800 text-white text-sm"
                  rows={2}
                  data-testid={`${category}-${key}-notes`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      <header className="border-b border-zinc-800 backdrop-blur-sm bg-black/30 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            className="text-zinc-300 hover:text-white hover:bg-white/10"
            onClick={() => navigate('/admin')}
            data-testid="back-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="text-right">
            <p className="text-zinc-400 text-sm">Inspecting</p>
            <p className="text-white font-semibold">{booking.carYear} {booking.carMake} {booking.carModel}</p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Space Grotesk' }}>
          Inspection Form
        </h1>

        <form onSubmit={handleSubmit}>
          {/* Vehicle Info */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 mb-8">
            <h2 className="text-2xl font-semibold text-white mb-6">Vehicle Information</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Label className="text-zinc-300">Mileage</Label>
                <Input
                  value={vehicleInfo.mileage}
                  onChange={(e) => setVehicleInfo({ ...vehicleInfo, mileage: e.target.value })}
                  placeholder="125079"
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="mileage-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Transmission</Label>
                <Input
                  value={vehicleInfo.transmission}
                  onChange={(e) => setVehicleInfo({ ...vehicleInfo, transmission: e.target.value })}
                  placeholder="Automatic"
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="transmission-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Interior Color</Label>
                <Input
                  value={vehicleInfo.interiorColor}
                  onChange={(e) => setVehicleInfo({ ...vehicleInfo, interiorColor: e.target.value })}
                  placeholder="Black"
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="interior-color-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Body Color</Label>
                <Input
                  value={vehicleInfo.bodyColor}
                  onChange={(e) => setVehicleInfo({ ...vehicleInfo, bodyColor: e.target.value })}
                  placeholder="Silver"
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="body-color-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Engine</Label>
                <Input
                  value={vehicleInfo.engine}
                  onChange={(e) => setVehicleInfo({ ...vehicleInfo, engine: e.target.value })}
                  placeholder="6 Cylinder"
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="engine-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Drive Type</Label>
                <Input
                  value={vehicleInfo.driveType}
                  onChange={(e) => setVehicleInfo({ ...vehicleInfo, driveType: e.target.value })}
                  placeholder="All Wheel Drive"
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="drive-type-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Fuel Type</Label>
                <Input
                  value={vehicleInfo.fuelType}
                  onChange={(e) => setVehicleInfo({ ...vehicleInfo, fuelType: e.target.value })}
                  placeholder="Gas"
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="fuel-type-input"
                />
              </div>
            </div>
          </div>

          {/* Photo Upload */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 mb-8">
            <h2 className="text-2xl font-semibold text-white mb-6">Inspection Photos</h2>
            <div className="mb-4">
              <Label htmlFor="photos" className="cursor-pointer inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg">
                <Camera className="w-5 h-5" />
                Upload Photos
              </Label>
              <Input
                id="photos"
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="photo-upload-input"
              />
            </div>
            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-4 mt-4">
                {photos.map((photo, idx) => (
                  <img key={idx} src={photo} alt={`Inspection ${idx + 1}`} className="rounded-lg border border-zinc-800" />
                ))}
              </div>
            )}
          </div>

          {/* Inspection Sections */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 mb-8">
            {renderCheckSection('Exterior', exteriorChecks, 'exterior')}
            {renderCheckSection('Interior', interiorChecks, 'interior')}
            {renderCheckSection('Mechanical', mechanicalChecks, 'mechanical')}
            {renderCheckSection('Road Test', roadTest, 'roadTest')}
          </div>

          {/* Summary & Notes */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8 mb-8">
            <h2 className="text-2xl font-semibold text-white mb-6">Summary & Notes</h2>
            <div className="space-y-6">
              <div>
                <Label className="text-zinc-300">Inspection Summary</Label>
                <Textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="Overall assessment of the vehicle condition..."
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  rows={4}
                  data-testid="summary-textarea"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Inspector Notes</Label>
                <Textarea
                  value={inspectorNotes}
                  onChange={(e) => setInspectorNotes(e.target.value)}
                  placeholder="Additional observations, recommendations, etc..."
                  className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                  rows={4}
                  data-testid="inspector-notes-textarea"
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg font-semibold rounded-lg"
            data-testid="submit-inspection-btn"
          >
            {loading ? 'Submitting...' : 'Submit Inspection Report'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default InspectionForm;